import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Target, Users, FileText, Zap, BarChart3, PhoneCall, Ban, Phone } from "lucide-react";
import { API, useAuth } from "@/App";
import { normalizeRole } from "@/lib/permissions";
import ClientCampaignsList from "@/components/clientdialer/ClientCampaignsList";
import ClientCampaignWizard from "@/components/clientdialer/ClientCampaignWizard";
import { useNavigate } from "react-router-dom";
import ClientDialerAnalytics from "@/components/clientdialer/ClientDialerAnalytics";
import { CallbacksPanel, DncPanel, ClientAgentView } from "@/components/clientdialer/ClientDialerPanels";
import { ComingSoon } from "@/components/clientdialer/ClientDialerUi";

export const CLIENT_DIALER_MANAGERS = ["super_admin", "crm_admin", "sales_manager"];
export const CLIENT_DIALER_ROLES = [...CLIENT_DIALER_MANAGERS, "sales_agent", "call_agent"];

const LEFT_TABS = [
  { id: "campaigns", label: "Campaigns", icon: Target, adminOnly: true },
  { id: "personal_campaigns", label: "Personal Campaigns", icon: Users, adminOnly: true, superOnly: true },
  { id: "scripts", label: "Scripts", icon: FileText },
  { id: "agents", label: "Agents", icon: Users, adminOnly: true },
  { id: "queue_ops", label: "Queue Ops", icon: Zap, adminOnly: true },
  { id: "analytics", label: "Analytics", icon: BarChart3, adminOnly: true },
];
const SOON = { personal_campaigns: "Personal Campaigns", scripts: "Scripts", agents: "Agents", queue_ops: "Queue Ops" };

function TodayPill({ s }) {
  if (!s || s.calls <= 0) return null;
  return (
    <div data-testid="cd-my-stats" title="Your client calls today (Eastern time)" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 11px", background: "#0F2942", color: "#fff", borderRadius: 999, fontSize: 11, fontWeight: 500, fontFamily: "var(--font)", marginRight: 6 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Phone style={{ width: 11, height: 11, color: "#93C5FD" }} /><span data-testid="cd-my-stats-calls" style={{ fontWeight: 700 }}>{s.calls}</span><span style={{ opacity: 0.7 }}>calls</span></span>
      <span style={{ opacity: 0.4 }}>·</span>
      <span><span data-testid="cd-my-stats-connect" style={{ fontWeight: 700, color: s.connect_rate >= 30 ? "#34c759" : s.connect_rate >= 15 ? "#FACC15" : "#fca5a5" }}>{s.connect_rate}%</span> <span style={{ opacity: 0.7 }}>connect</span></span>
      <span style={{ opacity: 0.4 }}>·</span>
      <span><span data-testid="cd-my-stats-demos" style={{ fontWeight: 700, color: "#A78BFA" }}>{s.demos_booked}</span> <span style={{ opacity: 0.7 }}>demo{s.demos_booked === 1 ? "" : "s"} booked</span></span>
    </div>
  );
}

function TabButton({ t, on, onClick }) {
  return (
    <button onClick={onClick} data-testid={`cd-tab-${t.id}`}
      style={{ padding: "10px 20px", fontSize: 12, fontWeight: on ? 600 : 400, cursor: "pointer", background: "none", border: "none", fontFamily: "var(--font)", color: on ? "var(--blue)" : "var(--text-tertiary)", borderBottom: on ? "2px solid var(--blue)" : "2px solid transparent", display: "flex", alignItems: "center", gap: 5 }}>
      <t.icon style={{ width: 13, height: 13 }} /> {t.label}
    </button>
  );
}

export default function ClientsDialer() {
  const { token, user } = useAuth();
  const role = normalizeRole(user?.role);
  const isManager = CLIENT_DIALER_MANAGERS.includes(role);
  const allowed = CLIENT_DIALER_ROLES.includes(role);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [view, setView] = useState(isManager ? "admin" : "agent");
  const [tab, setTab] = useState(isManager ? "campaigns" : "scripts");
  const [opts, setOpts] = useState({});
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(null);
  const navigate = useNavigate();
  const setWorking = (c) => navigate(`/clients-dialer/live?campaign_id=${c.id}`);
  const [cbs, setCbs] = useState({ callbacks: [], today: "" });
  const [dncRows, setDncRows] = useState([]);
  const [stats, setStats] = useState(null);
  const reqId = useRef(0);
  const load = useCallback(async () => {
    if (!allowed) return;
    const id = ++reqId.current;
    const [o, c, cb, dn, st] = await Promise.allSettled([
      axios.get(`${API}/client-dialer/options`, h), axios.get(`${API}/client-dialer/campaigns`, { ...h, params: { include_archived: true } }),
      axios.get(`${API}/client-dialer/callbacks`, h), isManager ? axios.get(`${API}/client-dialer/dnc`, h) : Promise.reject(new Error("skip")),
      axios.get(`${API}/client-dialer/my-stats`, h),
    ]);
    if (id !== reqId.current) return;
    if (o.status === "fulfilled") setOpts(o.value.data);
    if (c.status === "fulfilled") setCamps(c.value.data.campaigns);
    if (cb.status === "fulfilled") setCbs(cb.value.data);
    if (dn.status === "fulfilled") setDncRows(dn.value.data.clients);
    if (st.status === "fulfilled") setStats(st.value.data);
    setLoading(false);
  }, [token, allowed, isManager]); // eslint-disable-line react-hooks/exhaustive-deps -- h derives from token
  useEffect(() => { load(); }, [load]);

  if (!allowed) return <div data-testid="cd-no-access" className="page-content" style={{ padding: 40, color: "#64748b" }}>Clients Dialer isn't available for your role.</div>;
  const setStatus = async (c, status) => {
    try { await axios.post(`${API}/client-dialer/campaigns/${c.id}/status`, { status }, h); toast.success(`Campaign ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the campaign"); }
  };
  const liftDnc = async (r) => {
    if (!window.confirm(`Lift Do Not Call for ${r.clinic || `client #${r.client_id}`}?`)) return;
    try { await axios.delete(`${API}/client-dialer/dnc/${r.client_id}`, h); toast.success("Do Not Call lifted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't lift Do Not Call"); }
  };
  const left = LEFT_TABS.filter((t) => (isManager || !t.adminOnly) && (!t.superOnly || role === "super_admin"));
  const right = [{ id: "callbacks", label: `Callbacks (${cbs.callbacks.length})`, icon: PhoneCall }, ...(isManager ? [{ id: "dnc", label: `Do Not Call (${dncRows.length})`, icon: Ban }] : [])];
  const switchView = (v) => { setView(v); if (v === "admin" && !isManager) setTab("scripts"); };

  return (
    <div data-testid="clients-dialer-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients Dialer</h1>
          <p className="page-subtitle" data-testid="cd-subtitle">{view === "admin" ? "Campaign management, scripts, agents, and analytics" : "Your calling work queue and active sessions"}</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TodayPill s={stats} />
          <div style={{ display: "flex", background: "var(--gray-light)", borderRadius: 8, padding: 2 }} data-testid="cd-view-toggle">
            {[{ id: "admin", label: isManager ? "Admin" : "Scripts" }, { id: "agent", label: isManager ? "Agent View" : "My Queue" }].map((v) => (
              <button key={v.id} onClick={() => switchView(v.id)} data-testid={`cd-view-${v.id}`}
                style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: view === v.id ? 600 : 400, background: view === v.id ? "#fff" : "transparent", color: view === v.id ? "var(--text-primary)" : "var(--text-tertiary)", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>{v.label}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="page-content">
        {view === "agent" ? (
          <ClientAgentView campaigns={camps.filter((c) => (c.assigned_rep_ids || []).includes(user?.id))} callbacks={cbs.callbacks.filter((r) => r.rep_id === user?.id)} today={cbs.today} loading={loading} onStart={setWorking} />
        ) : (<>
          <div style={{ display: "flex", gap: 0, borderBottom: "0.5px solid var(--border)", marginBottom: 16 }} data-testid="cd-tabs">
            {left.map((t) => <TabButton key={t.id} t={t} on={tab === t.id} onClick={() => setTab(t.id)} />)}
            <div style={{ marginLeft: "auto", display: "flex" }}>{right.map((t) => <TabButton key={t.id} t={t} on={tab === t.id} onClick={() => setTab(t.id)} />)}</div>
          </div>
          {tab === "campaigns" && isManager && <ClientCampaignsList campaigns={camps} loading={loading} isManager={isManager} onNew={() => setWizard("new")} onStart={setWorking} onEdit={setWizard} onStatus={setStatus} />}
          {SOON[tab] && <ComingSoon testid={`cd-soon-${tab}`} title={SOON[tab]} />}
          {tab === "analytics" && isManager && <ClientDialerAnalytics opts={opts} />}
          {tab === "callbacks" && <CallbacksPanel rows={cbs.callbacks} today={cbs.today} loading={loading} />}
          {tab === "dnc" && isManager && <DncPanel rows={dncRows} onLift={liftDnc} loading={loading} />}
        </>)}
      </div>
      {wizard && <ClientCampaignWizard opts={opts} editing={wizard === "new" ? null : wizard} onClose={() => setWizard(null)} onSaved={() => { setWizard(null); load(); }} />}
    </div>
  );
}
